import { ActivityDataPoint, PeriodType, SentimentType, CustomDateRange } from '../types';
import { getSentimentColorValue } from './styleUtils';
import { isClipInPeriod } from './dateUtils';

/**
 * Group clippings by selected period type
 */
export function groupClippingsByPeriod(
  clippings: any[], 
  period: PeriodType,
  // We accept customDateRange but only use it explicitly for period label generation if needed
  // The main date range for period initialization comes from the actual data
  customDateRange?: CustomDateRange 
): ActivityDataPoint[] {
  // Define periods based on type
  const periodsMap = new Map<string, { total: number, sentiments: Record<SentimentType, number> }>();
  
  // Initialize sentiments object
  const initialSentiments: Record<SentimentType, number> = {
    'Very Positive': 0, 'Positive': 0, 'Neutral': 0, 'Negative': 0, 'Very Negative': 0
  };

  // --- Determine the TRUE date range from the clippings data --- 
  let absoluteMinDate: Date | null = null;
  let absoluteMaxDate: Date | null = null;
  if (clippings && clippings.length > 0) {
    const dates = clippings
      .map(clip => {
          const dateValue = clip.date || clip.publishDate || clip.publish_date || clip.created_at;
          return dateValue ? new Date(dateValue) : null;
      })
      .filter((date): date is Date => date !== null && !isNaN(date.getTime())); // Filter out invalid dates
      
    if (dates.length > 0) {
      absoluteMinDate = new Date(Math.min(...dates.map(d => d.getTime())));
      absoluteMaxDate = new Date(Math.max(...dates.map(d => d.getTime())));
      
      // Ensure start/end of day for range accuracy
      absoluteMinDate.setHours(0, 0, 0, 0);
      absoluteMaxDate.setHours(23, 59, 59, 999);
    }
  }

  // If no valid dates found, return empty array
  if (!absoluteMinDate || !absoluteMaxDate) {
    return [];
  }
  
  // Use absoluteMinDate and absoluteMaxDate for initializing periods
  const minDate = absoluteMinDate;
  const maxDate = absoluteMaxDate;
  
  // --- Initialize periods based on the TRUE date range --- 
  if (period === 'Monthly') {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let currentDate = new Date(minDate);
    currentDate.setDate(1); 
    while (currentDate <= maxDate) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const label = `${months[month]} ${year}`;
      if (!periodsMap.has(label)) {
        periodsMap.set(label, { total: 0, sentiments: { ...initialSentiments } });
      }
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  } else if (period === 'Quarterly') {
    let currentDate = new Date(minDate);
    currentDate.setDate(1); 
    while (currentDate <= maxDate) {
      const year = currentDate.getFullYear();
      const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
      const label = `Q${quarter} ${year}`;
      if (!periodsMap.has(label)) {
        periodsMap.set(label, { total: 0, sentiments: { ...initialSentiments } });
      }
      currentDate.setMonth(currentDate.getMonth() + 3);
    }
  } else if (period === 'Weekly') {
    let currentDate = new Date(minDate);
    // Start from the Sunday of the week containing minDate
    currentDate.setDate(currentDate.getDate() - currentDate.getDay());
    let weekCounter = 1; // Initialize week counter based on the first week with data

    // Find the first actual week label based on minDate
    let firstWeekEndDate = new Date(currentDate);
    firstWeekEndDate.setDate(currentDate.getDate() + 6);
    // Generate first label as date only
    let firstWeekLabel = firstWeekEndDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });

    while (currentDate <= maxDate) {
      const endOfWeek = new Date(currentDate);
      endOfWeek.setDate(currentDate.getDate() + 6);
      // Use MM/DD/YY format as the label itself
      const label = endOfWeek.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
      // Removed weekCounter and "Week X" prefix from label

      // Ensure we only add periods that overlap with the actual data range
      if (endOfWeek >= minDate && currentDate <= maxDate) {
        periodsMap.set(label, { total: 0, sentiments: { ...initialSentiments } });
      }

      currentDate.setDate(currentDate.getDate() + 7);
      weekCounter++;
    }
  } else { // Default to Daily
    let currentDate = new Date(minDate);
    while (currentDate <= maxDate) {
      const label = currentDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
      periodsMap.set(label, { total: 0, sentiments: { ...initialSentiments } });
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  // --- Count clippings for each period --- 
  if (clippings && clippings.length > 0) {
    clippings.forEach(clip => {
      const clipDateValue = clip.date || clip.publishDate || clip.publish_date || clip.created_at || null;
      if (!clipDateValue) return;
      
      try {
        const clipDate = new Date(clipDateValue);
        if (isNaN(clipDate.getTime())) return;

        // Crucially, check against the absolute range derived from data
        if (clipDate < absoluteMinDate! || clipDate > absoluteMaxDate!) return;
        
        let periodLabel: string | null = null;
        
        // --- Recalculate periodLabel based on clipDate and period type --- 
        // (This logic MUST align with the initialization logic above)
        if (period === 'Monthly') {
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          periodLabel = `${months[clipDate.getMonth()]} ${clipDate.getFullYear()}`;
        } else if (period === 'Quarterly') {
          const quarter = Math.floor(clipDate.getMonth() / 3) + 1;
          periodLabel = `Q${quarter} ${clipDate.getFullYear()}`;
        } else if (period === 'Weekly') {
            // Calculate the end date (Saturday) of the week the clipDate falls into
            let tempDate = new Date(clipDate);
            tempDate.setDate(tempDate.getDate() - tempDate.getDay() + 6); // Go to Sunday, then add 6 days to get Saturday
            // The label is now just the end date string
            periodLabel = tempDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });

            // Removed the loop searching through keys, as the label is deterministic
            
        } else { // Default to Daily
          periodLabel = clipDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
        }
        // --- End of Recalculation ---
        
        // If we have a period label and it exists in our map, increment the count
        if (periodLabel && periodsMap.has(periodLabel)) {
          const periodData = periodsMap.get(periodLabel)!;
          periodData.total += 1;
          
          // Update sentiment counts
          const sentiment = (clip.sentiment || 'neutral').toLowerCase();
          let sentimentType: SentimentType = 'Neutral'; 
          
          if (sentiment.includes('very positive') || sentiment.includes('very_positive')) {
            sentimentType = 'Very Positive';
          } else if (sentiment.includes('positive')) {
            sentimentType = 'Positive';
          } else if (sentiment.includes('very negative') || sentiment.includes('very_negative')) {
            sentimentType = 'Very Negative';
          } else if (sentiment.includes('negative')) {
            sentimentType = 'Negative';
          } else {
            sentimentType = 'Neutral';
          }
          
          if (!(sentimentType in periodData.sentiments)) {
             periodData.sentiments[sentimentType] = 0;
          }
          periodData.sentiments[sentimentType] += 1;
        }
      } catch (error) {
        console.error('Error processing clip date:', error);
      }
    });
  }
  
  // --- Convert map to array and sort --- 
  const result: ActivityDataPoint[] = Array.from(periodsMap.entries()).map(([label, data]) => ({
    label,
    value: data.total,
    sentiments: data.sentiments
  }));
  
  // Sort the results chronologically based on period type
  if (period === 'Monthly') {
    // Sort by month and year
    result.sort((a, b) => {
      const [aMonth, aYear] = a.label.split(' ');
      const [bMonth, bYear] = b.label.split(' ');
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const aMonthIndex = monthNames.indexOf(aMonth);
      const bMonthIndex = monthNames.indexOf(bMonth);
      
      if (aYear !== bYear) {
        return parseInt(aYear) - parseInt(bYear);
      }
      return aMonthIndex - bMonthIndex;
    });
  } else if (period === 'Quarterly') {
    // Sort by quarter and year
    result.sort((a, b) => {
      const aMatch = a.label.match(/Q(\d+) (\d+)/);
      const bMatch = b.label.match(/Q(\d+) (\d+)/);
      
      if (aMatch && bMatch) {
        const [, aQuarter, aYear] = aMatch;
        const [, bQuarter, bYear] = bMatch;
        
        if (aYear !== bYear) {
          return parseInt(aYear) - parseInt(bYear);
        }
        return parseInt(aQuarter) - parseInt(bQuarter);
      }
      return 0;
    });
  } else if (period === 'Weekly') {
    // Sort by end date (which is the label itself: MM/DD/YY)
    result.sort((a, b) => {
      // Split the date string labels
      const aParts = a.label.split('/');
      const bParts = b.label.split('/');

      if (aParts.length === 3 && bParts.length === 3) {
        // Construct Date objects from MM/DD/YY parts. Note: year 'YY' needs '20' prefix.
        try {
           // Explicitly parse parts to numbers first
           const aMonth = Number(aParts[0]) - 1; // Month is 0-indexed
           const aDay = Number(aParts[1]);
           const aYear = Number(`20${aParts[2]}`);

           const bMonth = Number(bParts[0]) - 1; // Month is 0-indexed
           const bDay = Number(bParts[1]);
           const bYear = Number(`20${bParts[2]}`);

           const aDate = new Date(aYear, aMonth, aDay);
           const bDate = new Date(bYear, bMonth, bDay);

           // Check if dates are valid before comparing
           if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
             return aDate.getTime() - bDate.getTime();
           }
        } catch (e) {
            console.error("Error parsing weekly date labels for sorting:", a.label, b.label, e);
        }
      }
      // Fallback sort by label string if parsing fails
      return a.label.localeCompare(b.label);
    });
  } else if (period === 'Daily') {
    // For daily date formats (MM/DD), sort by date
    result.sort((a, b) => {
      const aParts = a.label.split('/');
      const bParts = b.label.split('/');
      
      if (aParts.length === 2 && bParts.length === 2) {
        const aDate = new Date(new Date().getFullYear(), parseInt(aParts[0]) - 1, parseInt(aParts[1]));
        const bDate = new Date(new Date().getFullYear(), parseInt(bParts[0]) - 1, parseInt(bParts[1]));
        return aDate.getTime() - bDate.getTime();
      }
      return 0;
    });
  }
  
  return result;
} 